import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNextMessages } from '../../../actions/messages/getNextMessages';
import { currentOpenChatSelector } from '../../../selectors';
import { ERequestApiStatus } from '../../../types/appState';
import { TOpenedChat } from '../../../types/openedChats';

export const useMessagesScroll = (
  scrollToBottom: (isButtonClickScroll?: boolean) => void,
  getFirstUnreadMessage: () => HTMLDivElement | null,
  unreadRef: React.RefObject<HTMLDivElement | null>,
): { getNextMessagesPage: (scrollTop: number) => void } => {
  const currentChat = useSelector(currentOpenChatSelector);

  const dispatch = useDispatch();

  const { chatMessages, allMessagesLoaded, chatId, status } = currentChat || ({} as TOpenedChat);
  const isLoading = status === ERequestApiStatus.Pending;

  const getNextMessagesPage = React.useCallback(
    (scrollTop: number) => {
      if (!allMessagesLoaded && scrollTop <= 0 && !isLoading) {
        dispatch(getNextMessages());
      }
    },
    [allMessagesLoaded, dispatch, isLoading],
  );

  /**
   * Список отсортирован от старых сообщений к новым, поэтому смена последнего id — это новое сообщение.
   * Догрузка истории добавляет сообщения в начало и лента остаётся на месте
   */
  const lastMessageId = chatMessages?.[chatMessages.length - 1]?.messageId;

  // Синхронный сброс скролла до bottom при смене чата — должен выполниться
  // до ResizeObserver, который использует prev-рефы из ScrollContainer
  React.useLayoutEffect(() => {
    scrollToBottom();
  }, [chatId, scrollToBottom]);

  React.useEffect(() => {
    const firstUnreadMessage = getFirstUnreadMessage();
    if (unreadRef.current) {
      unreadRef.current.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
    } else if (firstUnreadMessage) {
      firstUnreadMessage.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
    } else {
      scrollToBottom();
    }
  }, [chatId, scrollToBottom, getFirstUnreadMessage, lastMessageId]);

  return { getNextMessagesPage };
};
